import { z } from 'zod';
import { fintsDrahtNachrichtSchema, fintsFreigabeArtSchema, fintsLaufZustandSchema, } from './fints.js';
import { ibanSchema } from './master-data.js';
import { centAmount, isoDate, uuid } from './posting.js';
/**
 * Die Verträge der SEPA-Lastschrift — Task 1 `[LS-grund]` des Plans
 * `docs/superpowers/plans/2026-09-15-lastschrift-umsetzung.md`, Spec
 * `docs/superpowers/specs/2026-09-15-lastschrift-design.md`, Abschnitte 2
 * und 6.
 *
 * **Kein Geldbetrag als `number`** (Hausregel 1): `centAmount` liefert
 * `Money`.
 *
 * **Zwei Schemas sind KEINE Endpunktverträge, sondern Leseschemas nach der
 * `sql:jsonb`-Konvention** (`lastschriftAnkuendigungSchema`,
 * `bankRueckmeldungSchema`): Die Folge-Dienste (`LS-einzug`, `LS-fints`)
 * lesen eine `jsonb`-Spalte über `Schema.safeParse(zeile.spalte)`, statt dem
 * gespeicherten Wert blind zu vertrauen — dieselbe Konvention, die
 * `pnpm sql:jsonb` gegen jede `jsonb`-Spalte mit Leseschema hält.
 */
// ---------------------------------------------------------------------------
// Aufzählungen
// ---------------------------------------------------------------------------
/** Der Lebenszyklus eines Mandats (Spec 2.1). */
export const sepaMandatStatusSchema = z.enum(['aktiv', 'widerrufen', 'erloschen']);
/** `FRST` beim ersten Einzug eines Mandats, `RCUR` danach (`D-7`). */
export const sepaSequenzSchema = z.enum(['FRST', 'RCUR']);
/** Der Lebenszyklus eines Einzugs (Spec 2.2). */
export const lastschriftEinzugStatusSchema = z.enum([
    'vorgemerkt',
    'in_datei',
    'eingereicht',
    'zurueckgegeben',
    'storniert',
]);
/** Der Lebenszyklus einer Datei (Spec 2.3). `eingereicht`/`hochgeladen` sind Endzustände. */
export const lastschriftDateiStatusSchema = z.enum([
    'erstellt',
    'eingereicht',
    'hochgeladen',
    'abgelehnt',
    'verworfen',
]);
/** Der Weg, auf dem eine Datei zur Bank gelangt (Spec 2.4). */
export const lastschriftEinreichungWegSchema = z.enum(['fints', 'hochgeladen']);
/** Das Ergebnis eines Einreichungsversuchs (Spec 2.4). */
export const lastschriftEinreichungErgebnisSchema = z.enum(['eingereicht', 'abgelehnt', 'abgebrochen']);
/** Der Einzugsstand, wie ihn `lastschrift_faellig` je Beleg zeigt. */
export const lastschriftEinzugsstandSchema = z.enum([
    'keiner',
    'vorgemerkt',
    'in_datei',
    'eingereicht',
    'zurueckgegeben',
    'storniert',
]);
// ---------------------------------------------------------------------------
// Mandat
// ---------------------------------------------------------------------------
/** Ein SEPA-Basislastschriftmandat, wie es die Kundenakte zeigt (Spec 2.1). */
export const sepaMandatSchema = z.object({
    id: uuid,
    customerId: uuid,
    mandatsreferenz: z.string().min(1).max(35),
    kontoinhaber: z.string().min(1),
    iban: z.string(),
    bic: z.string().nullable(),
    bankinstitut: z.string().nullable(),
    mandatsdatum: isoDate,
    art: z.literal('CORE'),
    status: sepaMandatStatusSchema,
    widerrufenAm: isoDate.nullable(),
    widerrufGrund: z.string().nullable(),
    letzterEinzugAm: isoDate.nullable(),
});
/**
 * Die Anfrage `POST /api/v1/customers/:id/sepa-mandate` (Spec Abschnitt 6).
 *
 * `iban` kommt aus {@link ibanSchema} (Auftrag `LS-ibaneingabe`) — die
 * Gruppenschreibweise, in der Banken eine IBAN drucken
 * (`DE06 7415 0000 0420 6359 48`), wird bereinigt statt abgewiesen. Die
 * Prüfziffer bleibt Sache der Datenbank (`sepa_mandat_iban_wellformed`).
 */
export const sepaMandatAnlageSchema = z.object({
    mandatsreferenz: z.string().min(1).max(35),
    kontoinhaber: z.string().min(1),
    iban: ibanSchema,
    bic: z.string().optional(),
    bankinstitut: z.string().optional(),
    mandatsdatum: isoDate,
});
/** Die Anfrage `POST /api/v1/sepa-mandate/:id/widerrufen`. */
export const sepaMandatWiderrufSchema = z.object({
    grund: z.string().min(1),
});
/**
 * Die Antwort von `GET /api/v1/customers/:id/sepa-mandate` — Task 2
 * `[LS-mandat]`, Spec Abschnitte 5 und 6.
 *
 * **Ein eigener Umschlag und keine blosse Liste.** Die Kundenakte zeigt das
 * eine aktive Mandat und die Historie der übrigen getrennt (Spec Abschnitt
 * 5); eine flache Liste zwänge die Oberfläche, `status` selbst auszuwerten,
 * um dieselbe Trennung nachzubauen — dieselbe Ansage müsste dann an zwei
 * Stellen stimmen.
 *
 * **`vorschlag` reist mit, statt dass die Oberfläche ihn selbst baut.**
 * `FS-<Kundennummer>-<JJJJMM>` ist Zeichenvorrat-sicher zu bilden (Spec 2.1);
 * das SEPA-Alphabet dafür zweimal zu führen — einmal hier, einmal im
 * Formular — wäre die Fehlerklasse, die dieses Haus mit "Träger tragender
 * Aussagen" ausdrücklich vermeidet.
 */
export const sepaMandatUebersichtSchema = z.object({
    aktiv: sepaMandatSchema.nullable(),
    historie: z.array(sepaMandatSchema),
    vorschlag: z.string(),
});
// ---------------------------------------------------------------------------
// Einzug
// ---------------------------------------------------------------------------
/** Ein vorgemerkter/eingereichter Einzug an einem Beleg (Spec 2.2). */
export const lastschriftEinzugSchema = z.object({
    id: uuid,
    documentId: uuid,
    mandatId: uuid,
    betrag: centAmount,
    faelligkeit: isoDate,
    sequenz: sepaSequenzSchema,
    endToEndId: z.string(),
    verwendungszweck: z.string(),
    status: lastschriftEinzugStatusSchema,
    dateiId: uuid.nullable(),
});
/** Die Anfrage `POST /api/v1/documents/:id/lastschrift/vormerken`. */
export const lastschriftVormerkenSchema = z.object({
    faelligkeit: isoDate.optional(),
});
/** Die Anfrage für Stornieren/Zurückgeben — beide verlangen einen Grund. */
export const lastschriftEinzugGrundSchema = z.object({
    grund: z.string().min(1),
});
/**
 * Die Anfrage `POST /api/v1/lastschrift/einzuege/:id/neu-datieren` (`D-24`,
 * Auftrag `LS-faelligkeitnachzug`).
 *
 * **`faelligkeit` ist optional, und das ist die Entscheidung.** Ohne Angabe
 * setzt der Dienst das FRÜHESTE zulässige Einzugsdatum — der Fall, für den
 * diese Route gebaut wurde: eine Vormerkung, die über ihre Fälligkeit hinweg
 * liegengeblieben ist. Mit Angabe wird gegen dieselbe Untergrenze geprüft und
 * nie stillschweigend hochgesetzt, genau wie beim Vormerken
 * ({@link lastschriftVormerkenSchema}).
 *
 * **Kein Grund im Rumpf.** Ein Storno ist eine Entscheidung gegen den Einzug
 * und verlangt deshalb einen; ein neues Datum ist die Fortsetzung desselben
 * Einzugs. Das Audit hält alte und neue Fälligkeit fest — das ist die Spur,
 * die ein Prüfer braucht.
 */
export const lastschriftNeuDatierenSchema = z.object({
    faelligkeit: isoDate.optional(),
});
// ---------------------------------------------------------------------------
// Datei
// ---------------------------------------------------------------------------
/** Eine SEPA-Sammellastschriftdatei (Spec 2.3). */
export const lastschriftDateiSchema = z.object({
    id: uuid,
    msgId: z.string(),
    faelligkeit: isoDate,
    sequenz: sepaSequenzSchema,
    anzahl: z.number().int().positive(),
    summe: centAmount,
    status: lastschriftDateiStatusSchema,
    bankAccountId: uuid.nullable(),
});
/** Die Anfrage `POST /api/v1/lastschrift/dateien/:id/hochgeladen`. */
export const lastschriftHochgeladenSchema = z.object({
    grund: z.string().optional(),
});
/** Die Anfrage `POST /api/v1/lastschrift/dateien/:id/verwerfen`. */
export const lastschriftVerwerfenSchema = z.object({
    grund: z.string().min(1),
});
// ---------------------------------------------------------------------------
// Einreichung
// ---------------------------------------------------------------------------
/** Ein Versuch, eine Datei bei der Bank einzureichen (Spec 2.4). */
export const lastschriftEinreichungSchema = z.object({
    id: uuid,
    dateiId: uuid,
    weg: lastschriftEinreichungWegSchema,
    laufId: z.string().nullable(),
    begonnenAm: z.string(),
    beendetAm: z.string().nullable(),
    ergebnis: lastschriftEinreichungErgebnisSchema.nullable(),
    tanVerfahren: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Fällig-Zeile (die Liste des Reiters, aus `lastschrift_faellig`)
// ---------------------------------------------------------------------------
export const lastschriftFaelligZeileSchema = z.object({
    documentId: uuid,
    belegnummer: z.string(),
    kundennummer: z.string(),
    customerId: uuid,
    offenerRest: centAmount,
    /**
     * `document.due_date` ist NULLABLE (Migration `0022`) — ein importierter
     * Altbeleg kann sie leer tragen (`0120`, „33 Belege due_date LEER").
     * `null` heisst hier: kein Fälligkeitsdatum am Beleg, nicht „unbekannt".
     */
    faelligkeit: isoDate.nullable(),
    mandatId: uuid,
    mandatsreferenz: z.string(),
    einzugsstand: lastschriftEinzugsstandSchema,
    /**
     * Die Kennung des Einzugs, auf den sich {@link einzugsstand} bezieht —
     * `null` bei `keiner` (Auftrag `LS-faelligkeitnachzug`).
     *
     * Ohne sie kann der Reiter keine Aktion an einem vorhandenen Einzug
     * anbieten, nur eine an seinem Beleg; „Neu datieren" (`D-24`) hängt aber am
     * Einzug und nicht am Beleg. Die Belegseite trägt dasselbe Feld seit
     * `LS-belegknoepfe` (`RechnungZahlstand.lastschrift.einzugId`).
     */
    einzugId: uuid.nullable(),
    /**
     * Die Fälligkeit DES EINZUGS — `null` bei `keiner`. Nicht dasselbe wie
     * {@link faelligkeit}: Das ist `document.due_date`, der Zahlungstermin der
     * Rechnung; dies ist der Tag, an dem die Bank abbuchen soll, und er kann
     * später liegen (`D-8`) oder inzwischen überholt sein (`D-24`).
     */
    einzugFaelligkeit: isoDate.nullable(),
});
// ---------------------------------------------------------------------------
// Leseschemas nach der `sql:jsonb`-Konvention
// ---------------------------------------------------------------------------
/**
 * `document.lastschrift_ankuendigung` (Migration `0232`) — beim Ausstellen
 * eingefroren, `D-5`, `E-1350`.
 */
export const lastschriftAnkuendigungSchema = z.object({
    mandatsreferenz: z.string(),
    glaeubigerId: z.string(),
    ibanMaskiert: z.string(),
    einzugsdatum: isoDate,
});
/** Ein einzelner HIRMG-/HIRMS-Eintrag der Bankantwort — Vorbild `Rueckmeldung` (`typen.ts`). */
export const bankRueckmeldungEintragSchema = z.object({
    code: z.number().int(),
    text: z.string(),
});
/**
 * `lastschrift_einreichung.bank_rueckmeldung` (Migration `0231`) — immer
 * GESCHWÄRZT (`schwaerzeDraht`/`geschwaerzteBankRueckmeldungen`, Task 4),
 * nie eine PIN oder TAN (`E-1365`).
 */
export const bankRueckmeldungSchema = z.object({
    meldungen: z.array(bankRueckmeldungEintragSchema),
});
// ---------------------------------------------------------------------------
// Listen (Task 3 `[LS-einzug]`)
// ---------------------------------------------------------------------------
/** `GET /api/v1/lastschrift/faellig` — die Liste des Reiters (Spec 2.6, `D-3`). */
export const lastschriftFaelligListSchema = z.object({
    faellig: z.array(lastschriftFaelligZeileSchema),
    /**
     * Das früheste Einzugsdatum, das dieses System heute zulässt (`D-8`) —
     * EINMAL je Antwort, nicht je Zeile, dieselbe Bauform wie
     * {@link lastschriftDateiListFintsSchema}.
     *
     * **Warum der Server ihn liefert und die Oberfläche ihn nicht rechnet.**
     * Er hängt am TARGET2-Kalender UND am `HIDMES`-Vorlauf des Instituts; beides
     * lebt im Server. Eine zweite Fassung im Browser wäre die Dopplung, gegen
     * die `bankarbeitstage.ts` seinen Kopfkommentar trägt — und sie wäre still
     * falsch, sobald das Institut seinen Vorlauf ändert.
     */
    fruehestesEinzugsdatum: isoDate,
});
/**
 * Eine Zeile der Dateiliste — die Datei plus ihr Einreichungsprotokoll
 * (Spec 5: „Dateiliste: … Zustand, Einreichungen").
 */
export const lastschriftDateiZeileSchema = lastschriftDateiSchema.extend({
    einreichungen: z.array(lastschriftEinreichungSchema),
});
/**
 * Das FinTS-Wissen der Dateiliste — EINMAL je Antwort, nicht je Datei.
 * Aufgabe `LS-belegknoepfe`.
 *
 * Ersetzt eine client-seitige Heuristik (`listBankAccounts().find(k =>
 * k.provider === 'fints' && k.isActive)`), die weder wusste, ob überhaupt
 * ein FinTS-Zugang eingerichtet ist, noch ob das Institut `HKDME` als
 * Geschäftsvorfall führt (`D-19`) — beides erfuhr der Nutzer bisher erst als
 * benannter Fehler beim Einreichen, nie als fehlender Knopf. Eine Liste ist
 * der richtige Träger für „kann diese Seite die Aktion anbieten", nicht eine
 * Vermutung aus einer zweiten Liste.
 *
 * `grund` ist nur bei `moeglich = false` gesetzt (`'kein FinTS-Zugang'` oder
 * `'Institut führt HKDME nicht'`) und sonst `null`.
 */
export const lastschriftDateiListFintsSchema = z.object({
    moeglich: z.boolean(),
    bankAccountId: uuid.nullable(),
    grund: z.string().nullable(),
});
/** `GET /api/v1/lastschrift/dateien`. */
export const lastschriftDateiListSchema = z.object({
    dateien: z.array(lastschriftDateiZeileSchema),
    fints: lastschriftDateiListFintsSchema,
    /**
     * Dasselbe Feld wie in {@link lastschriftFaelligListSchema}, aus demselben
     * Grund und für einen anderen Satz: Eine Datei in `erstellt`, deren
     * Fälligkeit davor liegt, ist nicht mehr einreichbar (`D-24`) — die Seite
     * sagt das, statt es der Bank zu überlassen.
     */
    fruehestesEinzugsdatum: isoDate,
});
/**
 * Antwort `POST /api/v1/lastschrift/dateien` — die neu erzeugten Dateien,
 * eine je Gruppe (Fälligkeit + Sequenz, `D-20`; mehrere, wenn eine Gruppe
 * das Postenmaximum aus `HIDMES` überschreitet).
 */
export const lastschriftDateiErzeugenAntwortSchema = z.object({
    dateien: z.array(lastschriftDateiSchema),
});
// Der Einreichungslauf (Auftrag `LS-fints`, `FintsLaufService.starteEinreichung`)
// ---------------------------------------------------------------------------
/**
 * Der Zustand eines FinTS-Einreichungslaufs, wie
 * `GET /lastschrift/dateien/:id/einreichung/:laufId` ihn ausgibt.
 *
 * **Dieselbe Form wie `fintsLaufSchema`** (`packages/contracts/src/fints.ts`),
 * absichtlich: derselbe Wartemechanismus (entkoppelte Freigabe mit den
 * Zeiten der Bank, oder ein eingetippter Code), dieselbe Oberflächenlage —
 * laufend, wartet auf Freigabe, fertig, abgebrochen. **Kein `bericht`** (es
 * gibt keine Umsätze zu berichten) und **`ergebnis` statt eines
 * Fehlerfeld-Auszugs** für den erfolgreichen Fall: `eingereicht`/`abgelehnt`
 * sind beides ABGESCHLOSSENE Läufe mit einer echten Bankantwort
 * (`zustand: 'fertig'`); nur eine fehlende Antwort — abgelaufene Freigabe,
 * abgerissene Verbindung, `LASTSCHRIFT_FINTS_OHNE_TAN` (`D-21`) — ist
 * `zustand: 'abgebrochen'` mit `ergebnis: 'abgebrochen'` und einem `fehler`.
 *
 * **Kein Feld dieses Schemas kann eine PIN oder eine TAN tragen** — derselbe
 * Vertrag wie `fintsLaufSchema` (`E-313`, `E-1365`): `.strict()`, und ein
 * Feld, das es nicht gibt, kann kein Dienst versehentlich füllen.
 */
export const lastschriftEinreichungLaufSchema = z
    .object({
    laufId: uuid,
    dateiId: uuid,
    bankAccountId: uuid,
    zustand: fintsLaufZustandSchema,
    /** Der Wortlaut des Instituts, unverändert. */
    aufforderung: z.string().nullable(),
    /** Der Name des TAN-Mediums, zur Anzeige. */
    tanMedium: z.string().nullable(),
    /** Sekunden bis zur nächsten Statusabfrage — aus den Angaben der Bank. */
    naechsteFrageIn: z.number().int().nonnegative().nullable(),
    /** Die wievielte Freigabeaufforderung gerade läuft, ab 1. */
    freigabeRunde: z.number().int().positive().nullable(),
    /** Wie viele Runden das Institut zulässt. */
    freigabeRundenMax: z.number().int().positive().nullable(),
    freigabeArt: fintsFreigabeArtSchema.nullable(),
    /** Der hinterlegte Medienname, den das Institut nicht anbietet (`V-fintsmedium`). */
    tanMediumNichtAngeboten: z.string().optional(),
    /** `null`, solange der Lauf noch keine Bankantwort oder keinen Abbruch trägt. */
    ergebnis: lastschriftEinreichungErgebnisSchema.nullable(),
    /** Die Rückmeldungen der Bank, geschwärzt — `null`, solange keine vorliegen. */
    bankRueckmeldung: z.array(bankRueckmeldungEintragSchema).nullable(),
    fehler: z
        .object({
        code: z.string(),
        meldung: z.string(),
        /**
         * Die Meldung des Wurfs, der hinter dem Code steckt — geschwärzt und
         * auf 500 Zeichen gekürzt (`LS-einreichdiagnose`, `E-1349`).
         *
         * **Warum sie ein eigenes Feld ist und nicht an `meldung` hängt.**
         * `meldung` ist eine der sieben Meldungen aus Spec 9, wörtlich und
         * ohne angehängten Grund — die Zusage aus `E-313`. Was eine fremde
         * Bibliothek sagt, steht deshalb daneben und nicht darin; die
         * Oberfläche zeigt es unter der Meldung, nicht in ihr. `undefined`,
         * solange der Abbruch einen eigenen Code dieses Hauses hatte: Dann
         * gibt es keine fremde Ursache.
         */
        ursache: z.string().optional(),
        /**
         * Die letzten gewechselten Nachrichten, geschwärzt — höchstens vier,
         * je Richtung auf 4.000 Zeichen gekürzt.
         *
         * **Kein Feld dieses Vertrags kann eine PIN, eine TAN oder die
         * Produkt-ID tragen, und dieses hier am wenigsten**: Der Draht
         * verlässt `dialog.ts` nur durch `schwaerzeDraht` (strukturell, ohne
         * den Wert zu kennen) und danach durch dieselbe Freitextschleuse wie
         * jedes andere Feld hier (`E-313`, `E-316`, `E-1359`). Einen
         * Rohmodus gibt es nicht.
         */
        draht: z.array(fintsDrahtNachrichtSchema).optional(),
    })
        .strict()
        .nullable(),
})
    .strict();
//# sourceMappingURL=lastschrift.js.map